/**
 * UTM parameters for in-product and marketing links.
 * @see https://en.wikipedia.org/wiki/UTM_parameters
 *
 * Use **utm_content** only when the destination URL does not distinguish the link
 * (e.g. three buttons all to `/`). Do not duplicate path info (like a game slug) in utm_content.
 */

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

/** Defaults for links shared from the PlayMath.org UI (not paid or external campaigns). */
export const UTM_SITE_SHARE_DEFAULTS: Pick<UtmParams, "utm_source" | "utm_medium"> = {
  utm_source: "playmath.org",
  utm_medium: "share",
};

/** Educators page — “Spread the joy of math” grid; same landing URL; pair with utm_content per CTA. */
export const UTM_CAMPAIGN_EDUCATORS = "edu";

/** Share control on the game page — game is identified by the `/games/[slug]` path. */
export const UTM_CAMPAIGN_GAME_SHARE = "game_share";

/** Distinguishes a Classroom CTA when the destination path is shared with other share buttons. */
export const UTM_CONTENT_GOOGLE_CLASSROOM = "google_classroom";

/**
 * Appends UTM query parameters to a URL. Existing params are preserved;
 * provided UTM keys overwrite any same-named keys on the URL.
 */
export function appendUtmParams(url: string, params: UtmParams): string {
  try {
    const u = new URL(url);
    for (const [key, value] of Object.entries(params) as [keyof UtmParams, string | undefined][]) {
      if (value != null && value !== "") {
        u.searchParams.set(key, value);
      }
    }
    return u.toString();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn("[appendUtmParams] Invalid URL; returning original string.", {
      url,
      error: message,
    });
    return url;
  }
}
